import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import React from "react";
import { useEffectOnce } from "react-use";
import { Constants } from "../../../../constants";
import { SubscriptionType, UpsellPanelSteps, UserAccessLevelType } from "../../../../constants/upsellPanelEnumsAndConstants";
import { isRequiredAccessLevelMatching } from "../../../../constants/upsellPanelHelpers";
import { useAuth } from "../../../../hooks/useAuth";
import { isNullOrUndefined } from "../../../../utils/utils";
import { useUpsellWidgetRedirect } from "../../hooks/useRedirectContext";
import { useUpsellWidgetContainer } from '../../hooks/useUpsellWidgetContainerContext';
import { useUpsellWidgetModel } from '../../hooks/useUpsellWidgetModelContext';
import { UpsellWidgetUserFormProvider } from "../../hooks/useUpsellWidgetUserFormContext";
import { useHasTakenTrialMutation } from "../../services/hooks/useCheckExistingUserQuery";
import {
    Step10LockedContent,
    Step11LockedContent,
    Step12EventRego,
    Step16ForgotPassword,
    Step17MFALogin,
    Step18PricingPanel,
    Step1SignUpForm,
    Step2SMSToken,
    Step3Login,
    Step4MobileUpdate,
    Step5UpgradeUser,
    Step6Final,
    Step7PaymentForm,
    Step8ExistingUserLogin,
    Step9PreProductActivation
} from "../Steps";
import { UpsellLoader } from "../Steps/FormControls/UpsellLoader";
import {
    Step1SignUpFormContent,
    Step2SMSTokenHeaderContent,
    Step3LoginContent,
    Step4MobileUpdateHeaderContent,
    Step5UpgradeUserContent,
    Step8ExistingUserLoginContent
} from "../Steps/HeaderContent";
import { MessageRedirectForm } from "./messageRedirectForm";
import SuccessMessageDisplay from "./successMessageDisplay";
import { UpsellWidgetSMSTokenTimerProvider } from "../../hooks/useUpsellWidgetSMSTokenTimerContext";

const UpSellWidgetContainer = () => {
    const { isAuthenticated } = useAuth();
    const model = useUpsellWidgetModel();
    const { currentStep, goToStep, bgCssClass, baseProps, updateBaseProps, headerFontClass, isUpsellContainerLoading, setIsUpsellContainerLoading, handleProductActivation, onSubmitError } = useUpsellWidgetContainer();
    const { redirectToUrl } = useUpsellWidgetRedirect();
    const shouldFix = false;

    const handleHasTakenTrialSuccess = (response) => {
        setIsUpsellContainerLoading(false);
        if (response.status === Constants.RequestStatus.SUCCESS) {
            const hasTakenTrial = response.hasTakenTrial;
            updateBaseProps({
                user: {
                    ...baseProps.user,
                    hasTakenTrial: hasTakenTrial
                },
                byPassPhoneVerification: response.userIsPhoneVerified,
                chargifySubscription: response.chargifySubscription,
                card: {
                    expiryMM: response.chargifySubscription?.cardMM,
                    expiryYY: response.chargifySubscription?.cardYY,
                    number: response.chargifySubscription?.cardNumber,
                    chargifySubscriptionId: response.chargifySubscription?.chargifySubscriptionId,
                }
            });

            if (hasTakenTrial && model.showPricingPanel) {
                goToStep(UpsellPanelSteps.Step18PricingPanel);
            }
            else if (model.productHandle === SubscriptionType.SUBSCRIPTION_TRIAL_PRODUCT) {
                if (hasTakenTrial && baseProps.user?.subscriptionStatus != Constants.CHARGIFY_SUBSCRIPTION_PAST_DUE_STATUS) {
                    if (updateCTA(response)) {
                        goToStep(UpsellPanelSteps.Step5UpgradeUser);
                    }
                }
                else {
                    handleExistingUser(response);
                }
            }
            else {
                if (isRequiredAccessLevelMatching(model?.requiredAccessLevel, UserAccessLevelType.Professional)) {
                    if (updateCTA(response)) {
                        goToStep(UpsellPanelSteps.Step5UpgradeUser);
                    }
                }
                else if (isNullOrUndefined(model.eventId) || model.eventId === 0) {
                    handleExistingUser(response);
                }
            }
        }
    }
    const queryClient = useQueryClient();

    const hasTakenTrialMutation = useHasTakenTrialMutation(
        handleHasTakenTrialSuccess,
        (error) => onSubmitError(error)
    );

    const updateCTA = () => {
        let upgradeCTAText = "Join now and unlock our market beating research on undervalued ASX stocks from our team of analysts.";
        let upgradeHeading = `${Constants.INTELLIGENT_INVESTOR} Membership`;
        if (isRequiredAccessLevelMatching(model?.requiredAccessLevel, UserAccessLevelType.Essentials)) {
            upgradeCTAText = "Join today to unlock our content including Alan Kohler’s CEO Interviews, Weekend Brief and much much more."
            upgradeHeading = `${Constants.EUREKA_REPORT} Membership`;
        }
        else if (isRequiredAccessLevelMatching(model?.requiredAccessLevel, UserAccessLevelType.Professional)) {
            upgradeCTAText = `This article is only available to members with a monthly or annual ${Constants.INTELLIGENT_INVESTOR} subscription. Join now and unlock our market beating research on undervalued ASX stocks from our team of analysts.`;
        }

        updateBaseProps({
            content: {
                ...baseProps.content,
                upgradeHeading: upgradeHeading
            },
            upgradeCTA: "LEARN MORE",
            upgradeCTAText: upgradeCTAText,
            upgradeURL: '/membership-pricing'
        });
        return true;
    }
    const handleExistingUser = (response) => {
        if (isRequiredAccessLevelMatching(model?.requiredAccessLevel, UserAccessLevelType.Professional)) {
            goToStep(UpsellPanelSteps.Step5UpgradeUser);
        } else if (response.userIsPhoneVerified) {
            handleProductActivation(response, redirectToUrl, model);
        } else if (model.subscriptionStatus === Constants.CHARGIFY_SUBSCRIPTION_PAST_DUE_STATUS) {
            const nextStep = model.subscriptionPaymentUpdated === "true"
                ? UpsellPanelSteps.Step11PastDueStatusPaymentDetailsUpdated
                : UpsellPanelSteps.Step10PastDueStatusPaymentFailed;
            goToStep(nextStep);
        } else {
            goToStep(UpsellPanelSteps.Step4MobileUpdate);
        }
    };

    useEffectOnce(() => {
        if (isAuthenticated) {
            setIsUpsellContainerLoading(true);
            // Check if the result is already cached
            const cachedData = queryClient.getQueryData(['hasTakenTrial', model.requiredAccessLevel]);

            if (cachedData) {
                // Use cached data directly
                handleHasTakenTrialSuccess(cachedData);
            } else {
                // Otherwise, make the API call
                hasTakenTrialMutation.mutate(model.requiredAccessLevel);
            }
        }
    })

    return (
        <div className={classNames(
            "upsell-panel-custom-outer",
            { "borderblur": model.borderBlur, "promo-signup-position-fix": shouldFix },
            bgCssClass,
            model.outerClass,
            model.trackingClass,
            model.upsellOuterClass
        )}
        >
            <div className="loader-container">
                {isUpsellContainerLoading && <UpsellLoader />}
                <div className={classNames(
                    "upsell-container border-rad-8",
                    { "shadow-24-96": !model.usePopupOverlay && !model.isEurekaFund }
                )}
                >
                    <div className={classNames(
                        "upsell-panel-custom border-rad-8",
                        model.panelClass,
                        {
                            [model.backgroundImageClass]: model.isCompetitionPage,
                            [model.textPrimaryColor]: model.isCompetitionPage,
                            "bg-white text-color-deep-blue-2": !model.isCompetitionPage,
                            "eureka-fund-panel": model.isEurekaFund,
                            "mt-0": model.usePopupOverlay,
                        }
                    )} style={{ minHeight: "270px" }}
                    >
                        {model.backgroundImageClass && (
                            <div className={model.backgroundImageClass}></div>
                        )}
                        <div className="upsell-panel-content-container">
                            <div className="upsell-panel-top">
                                {model.eventId && (
                                    <button type="button"
                                        className="close t-r"
                                        onClick={() => console.log("Close Modal")}
                                        data-bs-dismiss="modal"
                                    > &times; </button>
                                )}
                                <div className="upsell-container-content">
                                    <SuccessMessageDisplay successMessage={baseProps?.successMessage} />
                                </div>

                                {currentStep !== UpsellPanelSteps.Step12EventRego && (
                                    <div className={classNames(
                                        "upsell-panel-title",
                                        {
                                            "pb-2 px-0": model.usePopupOverlay || model.isEurekaFund,
                                            "pb-0": currentStep === UpsellPanelSteps.Step7PaymentForm,
                                        }
                                    )} >
                                        {model.displayQuestionAllSteps && model.content.question && (
                                            <div className={classNames(headerFontClass, "font-circular-std-bold mb-2")} dangerouslySetInnerHTML={{ __html: model.content.question }}></div>
                                        )}
                                        {currentStep === UpsellPanelSteps.Step1SignUp && <Step1SignUpFormContent />}
                                        {currentStep === UpsellPanelSteps.Step2SMSToken && <Step2SMSTokenHeaderContent />}
                                        {currentStep === UpsellPanelSteps.Step3Login && <Step3LoginContent />}
                                        {currentStep === UpsellPanelSteps.Step4MobileUpdate && <Step4MobileUpdateHeaderContent />}
                                        {currentStep === UpsellPanelSteps.Step5UpgradeUser && <Step5UpgradeUserContent />}
                                        {currentStep === UpsellPanelSteps.Step8ExistingUserLogin && <Step8ExistingUserLoginContent />}
                                    </div>
                                )}
                            </div>

                            <div className="upsell-panel-body upsell-container-content">
                                {
                                    hasTakenTrialMutation.isPending ? <div>Loading...</div> :
                                        <>
                                            <UpsellWidgetUserFormProvider>
                                                {currentStep === UpsellPanelSteps.Step1SignUp && <Step1SignUpForm />}
                                                {currentStep === UpsellPanelSteps.Step3Login && <Step3Login />}
                                                {currentStep === UpsellPanelSteps.Step16ForgotPassword && <Step16ForgotPassword />}
                                            </UpsellWidgetUserFormProvider>
                                            <UpsellWidgetSMSTokenTimerProvider>
                                                {currentStep === UpsellPanelSteps.Step2SMSToken && <Step2SMSToken />}
                                                {currentStep === UpsellPanelSteps.Step4MobileUpdate && <Step4MobileUpdate />}
                                            </UpsellWidgetSMSTokenTimerProvider>
                                            {currentStep === UpsellPanelSteps.Step5UpgradeUser && <Step5UpgradeUser />}
                                            {currentStep === UpsellPanelSteps.Step6Final && <Step6Final />}
                                            {currentStep === UpsellPanelSteps.Step7PaymentForm && <Step7PaymentForm />}
                                            {currentStep === UpsellPanelSteps.Step8ExistingUserLogin && <Step8ExistingUserLogin />}
                                            {currentStep === UpsellPanelSteps.Step9PreProductActivation && <Step9PreProductActivation />}
                                            {currentStep === UpsellPanelSteps.Step10LockedContent && <Step10LockedContent />}
                                            {currentStep === UpsellPanelSteps.Step11LockedContent && <Step11LockedContent />}
                                            {currentStep === UpsellPanelSteps.Step12EventRego && <Step12EventRego />}
                                            {currentStep === UpsellPanelSteps.Step17MFALogin && <Step17MFALogin />}
                                            {currentStep === UpsellPanelSteps.Step18PricingPanel && model.ShowPricingPanel && <Step18PricingPanel />}
                                            <MessageRedirectForm />
                                        </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpSellWidgetContainer;
